import { useState, useMemo } from 'react';
import { useCreateGroup } from '@/hooks/useGroups';
import { GROUP_COLORS, DEFAULT_CURRENCY, FALLBACK_CURRENCIES_DATA } from '@sharecost/shared';
import { X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CreateGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (groupId: string) => void;
}

export function CreateGroupDialog({ open, onClose, onCreated }: CreateGroupDialogProps) {
  const { t } = useTranslation();
  const createGroup = useCreateGroup();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [color, setColor] = useState<string>(GROUP_COLORS[0]);

  const sortedCurrencies = useMemo(() => {
    return [...FALLBACK_CURRENCIES_DATA].sort((a, b) => {
      if (a.code === DEFAULT_CURRENCY) return -1;
      if (b.code === DEFAULT_CURRENCY) return 1;
      return a.code.localeCompare(b.code);
    });
  }, []);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const group = await createGroup.mutateAsync({
        name,
        description: description || undefined,
        currency_code: currency,
        color,
      });
      setName('');
      setDescription('');
      onClose();
      onCreated?.(group.id);
    } catch {
      // Error is available via createGroup.error
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md max-h-full overflow-y-auto rounded-xl bg-surface-primary p-6 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">{t('groups:createModal.title')}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-text-quaternary hover:text-text-secondary">
            <X className="h-5 w-5" />
          </button>
        </div>

        {createGroup.error && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
            {createGroup.error instanceof Error ? createGroup.error.message : t('web:createGroupDialog.failedToCreateGroup')}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="group-name" className="block text-sm font-medium text-text-secondary">
              {t('web:common.groupName')}
            </label>
            <input
              id="group-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              placeholder={t('web:common.eGTripToBarcelona')}
              className="mt-1 w-full rounded-lg border border-border-secondary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>

          <div>
            <label htmlFor="group-desc" className="block text-sm font-medium text-text-secondary">
              {t('groups:createModal.field.description')}
            </label>
            <input
              id="group-desc"
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('web:common.whatIsThisGroupFor')}
              className="mt-1 w-full rounded-lg border border-border-secondary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>

          <div>
            <label htmlFor="group-currency" className="block text-sm font-medium text-text-secondary">
              {t('expenses:form.picker.currency')}
            </label>
            <select
              id="group-currency"
              value={currency}
              onChange={e => setCurrency(e.target.value)}
              required
              className="mt-1 w-full rounded-lg border border-border-secondary bg-surface-primary pl-3 pr-10 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            >
              {sortedCurrencies.map(c => (
                <option key={c.code} value={c.code}>
                  {c.code} ({c.symbol})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">{t('expenses:category.color')}</label>
            <div className="flex gap-2 flex-wrap">
              {GROUP_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`h-8 w-8 rounded-full transition-all ${
                    color === c ? 'ring-2 ring-offset-2 ring-offset-surface-primary ring-text-quaternary scale-110' : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-border-secondary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-hover"
            >
              {t('common:button.cancel')}
            </button>
            <button
              type="submit"
              disabled={createGroup.isPending || !name.trim()}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {createGroup.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common:button.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
